import {
  ChangeDetectionStrategy,
  Component,
  computed,
  inject,
  input,
} from "@angular/core";
import { toSignal } from "@angular/core/rxjs-interop";
import {
  FormControl,
  FormGroup,
  ReactiveFormsModule,
  Validators,
} from "@angular/forms";
import { RouterModule } from "@angular/router";
import { PHONE_PATTERN } from "@optee/constants";
import { ButtonComponent } from "@optee/ui/components/button/button.component";
import { FormFieldComponent } from "@optee/ui/components/form/form-field/form-field.component";
import { Checkbox } from "primeng/checkbox";
import { InputText } from "primeng/inputtext";
import { Select } from "primeng/select";
import { TextareaModule } from "primeng/textarea";
import { HubspotService } from "../../services/hubspot.service";

const JOBTITLE_OPTIONS = [
  {
    label: "Gestionnaire d'actifs immobilier",
    value: "Gestionnaire d'actifs immobilier",
  },
  {
    label: "Gestionnaire d'actifs tertiaires",
    value: "Gestionnaire d’actifs tertiaires",
  },
  {
    label: "Gestionnaire d'actifs immobiliers Senior",
    value: "Gestionnaire d’actifs immobiliers Senior",
  },
  {
    label: "Gestionnaire d'actifs tertiaires Senior",
    value: "Gestionnaire d’actifs tertiaires Senior",
  },
  {
    label: "Directeur de l'asset management",
    value: "Directeur de l’asset management",
  },
  {
    label: "Gestionnaire de copropriétés",
    value: "Gestionnaire de copropriétés",
  },
  { label: "Responsable technique", value: "Responsable technique" },
  { label: "Directeur technique", value: "Directeur technique" },
  {
    label: "Responsable de programmes immobiliers",
    value: "Responsable de programmes immobiliers",
  },
  { label: "Property manager", value: "Property manager" },
  { label: "Propriétaire exploitant", value: "Propriétaire exploitant" },
  { label: "Autre", value: "Autre" },
] as const;

type Jobtitle = (typeof JOBTITLE_OPTIONS)[number]["value"];

const INDUSTRY_OPTIONS = [
  { label: "Entreprise de travaux", value: "Entreprise de travaux" },
  {
    label: "Gestion de copropriétés",
    value: "Gestion de copropriétés",
  },
  {
    label: "Gestion d'actifs immobiliers",
    value: "Gestion d'actifs immobiliers",
  },
  { label: "Santé", value: "Santé" },
  { label: "Éducation", value: "Education" },
  { label: "Hôtellerie-Restauration", value: "Hôtellerie-Restauration" },
  { label: "Architecte / BET", value: "Architecte/BET" },
  { label: "Autre", value: "Autre" },
] as const;

type Industry = (typeof INDUSTRY_OPTIONS)[number]["value"];

@Component({
  selector: "swc-form-hubspot",
  host: {
    class: "block h-full",
  },
  template: `
    <div
      class="flex flex-col gap-4 p-6 text-white xl:hidden"
      [class]="{
        hidden: type() === 'lite',
      }"
    >
      <span class="text-xl font-semibold leading-9">
        Atteignez vos objectifs d'efficacité énergétique avec Optee.
      </span>
      <p>
        Plus de 200 clients nous font déjà confiance. Renseignez vos
        informations pour être rappelé par notre équipe.
      </p>
    </div>
    <form
      class="h-full bg-white p-6 xl:items-center xl:justify-center"
      (ngSubmit)="submitHubSpotForm()"
      [class]="{
        'xl:px-32': type() === 'extended',
      }"
      [formGroup]="hubSpotForm"
    >
      <div class="font-display grid grid-cols-1 gap-6 md:grid-cols-2">
        <oui-form-field
          name="firstname"
          label="Prénom"
          [control]="hubSpotForm.controls.firstname"
        >
          <input
            id="firstname"
            name="firstname"
            autocomplete="firstname"
            fluid
            pInputText
            placeholder="Ex : François"
            pSize="large"
            required
            type="text"
            [formControl]="hubSpotForm.controls.firstname"
          />
        </oui-form-field>

        <oui-form-field
          name="lastname"
          label="Nom"
          [control]="hubSpotForm.controls.lastname"
        >
          <input
            id="lastname"
            name="lastname"
            autocomplete="lastname"
            fluid
            pInputText
            placeholder="Ex : Dupont"
            pSize="large"
            required
            type="text"
            [formControl]="hubSpotForm.controls.lastname"
          />
        </oui-form-field>

        <oui-form-field
          name="email"
          label="Adresse mail"
          [control]="hubSpotForm.controls.email"
        >
          <input
            id="email"
            name="email"
            autocomplete="email"
            fluid
            pInputText
            placeholder="Ex : françois.dupont@gmail.com"
            pSize="large"
            required
            type="email"
            [formControl]="hubSpotForm.controls.email"
          />
        </oui-form-field>

        <oui-form-field
          name="mobilephone"
          label="Numéro de téléphone"
          [control]="hubSpotForm.controls.mobilephone"
        >
          <input
            id="mobilephone"
            name="mobilephone"
            autocomplete="mobilephone"
            fluid
            pInputText
            placeholder="Ex : 06 99 78 34 23"
            pSize="large"
            required
            type="tel"
            [formControl]="hubSpotForm.controls.mobilephone"
          />
        </oui-form-field>

        <oui-form-field
          name="company"
          label="Société"
          [control]="hubSpotForm.controls.company"
        >
          <input
            id="company"
            name="company"
            autocomplete="company"
            fluid
            pInputText
            placeholder="Ex : Dupont Société"
            pSize="large"
            required
            type="text"
            [formControl]="hubSpotForm.controls.company"
          />
        </oui-form-field>

        <oui-form-field
          name="numemployees"
          label="Nombre d'employés"
          [control]="hubSpotForm.controls.numemployees"
        >
          <p-select
            appendTo="body"
            fluid
            optionLabel="label"
            optionValue="value"
            placeholder="Sélectionner"
            size="large"
            [formControl]="hubSpotForm.controls.numemployees"
            [options]="EMPLOYEE_OPTIONS"
          />
        </oui-form-field>

        <oui-form-field
          name="industry"
          label="Secteur d’activité"
          [control]="hubSpotForm.controls.industry"
        >
          <p-select
            appendTo="body"
            fluid
            optionLabel="label"
            optionValue="value"
            placeholder="Sélectionner"
            required
            size="large"
            [formControl]="hubSpotForm.controls.industry"
            [options]="INDUSTRY_OPTIONS"
          />
        </oui-form-field>

        <oui-form-field
          name="jobtitle"
          label="Poste"
          [class]="{
            hidden: !showJobtitleField(),
          }"
          [control]="hubSpotForm.controls.jobtitle"
        >
          <p-select
            appendTo="body"
            fluid
            optionLabel="label"
            optionValue="value"
            placeholder="Sélectionner"
            size="large"
            [formControl]="hubSpotForm.controls.jobtitle"
            [options]="JOBTITLE_OPTIONS"
            [required]="showJobtitleField()"
          />
        </oui-form-field>
      </div>

      <div class="mt-6 flex flex-col gap-6">
        <oui-form-field
          name="message"
          label="Motif de la demande"
          [class]="{
            hidden: type() === 'lite',
          }"
          [control]="hubSpotForm.controls.message"
        >
          <textarea
            class="w-full resize-none"
            id="message"
            fluid
            placeholder="Ex : Demande de DTG, développement de mon réseau pro..."
            pTextarea
            rows="2"
            [formControl]="hubSpotForm.controls.message"
          ></textarea>
        </oui-form-field>

        <oui-form-field
          name="acceptCgv"
          label=""
          [control]="hubSpotForm.controls.acceptCgv"
        >
          <div
            class="text-primary-900 flex items-start space-x-2 text-sm font-normal leading-5 tracking-[0.28px]"
          >
            <p-checkbox
              required
              [binary]="true"
              [formControl]="hubSpotForm.controls.acceptCgv"
            />
            <label>
              En remplissant ce formulaire, j’accepte que mes informations
              personnelles saisies soient utilisées par Optee à des fins de
              prospection commerciale. Pour plus d’information, cliquez
              <a class="underline" routerLink="/politique-de-confidentialite">
                ici
              </a>
            </label>
          </div>
        </oui-form-field>

        @if (type() === "lite") {
          <oui-button type="submit" variant="primary">Envoyer</oui-button>
        } @else {
          <oui-button
            type="submit"
            variant="primary"
            [disabled]="hubSpotForm.invalid"
          >
            Demander une démo
          </oui-button>
        }

        <div
          class="mb-6 text-sm font-normal leading-5 text-gray-600"
          [class]="{
            hidden: type() === 'lite',
          }"
        >
          Vous utilisez déjà Optee ?
          <a
            class="font-display text-primary-500 font-medium underline"
            href="https://app.optee.io"
          >
            Connectez-vous
          </a>
        </div>
      </div>
    </form>
  `,
  imports: [
    ReactiveFormsModule,
    FormFieldComponent,
    RouterModule,
    InputText,
    ButtonComponent,
    Checkbox,
    Select,
    TextareaModule,
  ],
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export default class FormHubspotComponent {
  type = input<"extended" | "lite">("extended");
  protected readonly hubspotService = inject(HubspotService);

  protected readonly EMPLOYEE_OPTIONS = [
    { label: "1-5", value: "1-5" },
    { label: "5-25", value: "5-25" },
    { label: "25-50", value: "25-50" },
    { label: "50-100", value: "50-100" },
    { label: "100-500", value: "100-500" },
    { label: "500-1000", value: "500-1000" },
    { label: "1000+", value: "1000+" },
  ];

  JOBTITLE_OPTIONS = [...JOBTITLE_OPTIONS];
  INDUSTRY_OPTIONS = [...INDUSTRY_OPTIONS];

  hubSpotForm = new FormGroup({
    firstname: new FormControl("", [Validators.required]),
    lastname: new FormControl("", [Validators.required]),
    email: new FormControl("", [Validators.required, Validators.email]),
    mobilephone: new FormControl("", [Validators.pattern(PHONE_PATTERN)]),
    company: new FormControl(""),
    numemployees: new FormControl(""),
    jobtitle: new FormControl<Jobtitle | null>(null),
    industry: new FormControl<Industry | null>(null, [Validators.required]),
    message: new FormControl(""),
    acceptCgv: new FormControl(false, [Validators.requiredTrue]),
  });

  industryChangeSignal = toSignal(
    this.hubSpotForm.controls.industry.valueChanges,
  );

  showJobtitleField = computed(() => {
    const industry = this.industryChangeSignal();
    return !!industry && industry !== "Entreprise de travaux";
  });

  async submitHubSpotForm() {
    if (!this.hubSpotForm.valid) {
      this.hubSpotForm.markAllAsTouched();
      return;
    }

    const formValues = this.hubSpotForm.value;
    const body = {
      fields: [
        { name: "firstname", value: formValues.firstname },
        { name: "lastname", value: formValues.lastname },
        { name: "email", value: formValues.email },
        { name: "mobilephone", value: formValues.mobilephone },
        { name: "company", value: formValues.company },
        { name: "numemployees", value: formValues.numemployees },
        { name: "type_de_poste", value: formValues.jobtitle },
        { name: "secteur_d_activite", value: formValues.industry },
        { name: "message", value: formValues.message },
      ],
      context: {
        pageUri: window.location.href,
        pageName: document.title,
      },
    };

    this.hubspotService.DemoRequest(body);
  }
}
